import { SubscriptionPlans } from '@vimeo/gctl-api/models/components/schemas/subscription-plans/SubscriptionPlans';
import { translate } from '@vimeo/i18n-runtime';

export type GetSubscriptionPlansFilters = (
  | 'advanced'
  | 'business'
  | 'live_premium'
  | 'plus'
  | 'pro'
  | 'pro_unlimited'
  | 'standard'
  | 'starter'
)[];

export interface SubPlanQuery {
  bypassTierHierarchy?: boolean;
  campaignId?: string;
  filter?: GetSubscriptionPlansFilters;
  promos?: string;
  vuid?: string;
}

const UPLOAD_FILE_LIMIT = {
  BASIC: 524288000,
  MAX_BASIC_USER_STORAGE: 5368709120,
  PLUS: 5368709120,
  PRO: 21474836480,
  PRO_U: 3298534883328,
  BUSINESS: 5497558138880,
  LIVE_PREMIUM: 7696581394432,
} as const;

type StorageType = {
  weekly: string | null;
  yearly: string | null;
  total: string | null;
};
type plansDataStorageType = Partial<Record<SubscriptionPlans['tier'], StorageType>>;

/**
 * Reduces a supplied amount of bytes into the closest logical magnitude.
 *
 * @param {number} bytes The total number of bytes to format
 * @param {boolean} showDecimals Show decimals value on return bytes
 * @return {string} Formatted bytes with the appropriate measurement prefix
 */
export const formatBytes = (bytes: number, showDecimals = true): string => {
  let i = 0;
  const fixedDecimal = showDecimals ? 2 : 0;

  for (i = 0; bytes >= 1024 && i < 4; i++) {
    bytes /= 1024;
  }
  if (i === 0) {
    return '0';
  }
  const formattedBytes = [
    translate({
      singular: '{BYTE_SIZE}B',
      replacements: { BYTE_SIZE: bytes.toFixed(fixedDecimal) },
    }),
    translate({
      singular: '{BYTE_SIZE}KB',
      replacements: { BYTE_SIZE: bytes.toFixed(fixedDecimal) },
    }),
    translate({
      singular: '{BYTE_SIZE}MB',
      replacements: { BYTE_SIZE: bytes.toFixed(fixedDecimal) },
    }),
    translate({
      singular: '{BYTE_SIZE}GB',
      replacements: { BYTE_SIZE: bytes.toFixed(fixedDecimal) },
    }),
    translate({
      singular: '{BYTE_SIZE}TB',
      replacements: { BYTE_SIZE: bytes.toFixed(fixedDecimal) },
    }),
  ];

  return formattedBytes[i];
};

export const plansDataStorage: plansDataStorageType = {
  basic: {
    weekly: formatBytes(UPLOAD_FILE_LIMIT.BASIC),
    yearly: null,
    total: formatBytes(UPLOAD_FILE_LIMIT.MAX_BASIC_USER_STORAGE),
  },
  plus: {
    weekly: formatBytes(UPLOAD_FILE_LIMIT.PLUS),
    yearly: formatBytes(UPLOAD_FILE_LIMIT.PLUS * 50),
    total: null,
  },
  pro: {
    weekly: formatBytes(UPLOAD_FILE_LIMIT.PRO),
    yearly: formatBytes(UPLOAD_FILE_LIMIT.PRO * 52),
    total: formatBytes(UPLOAD_FILE_LIMIT.PRO * 52 * 3),
  },
  proUnlimited: {
    weekly: null,
    yearly: formatBytes(UPLOAD_FILE_LIMIT.PRO_U),
    total: null,
  },
  business: {
    weekly: null,
    yearly: null,
    total: formatBytes(UPLOAD_FILE_LIMIT.BUSINESS),
  },
  livePremium: {
    weekly: null,
    yearly: null,
    total: formatBytes(UPLOAD_FILE_LIMIT.LIVE_PREMIUM),
  },
  enterprise: {
    weekly: null,
    yearly: null,
    total: null,
  },
  starter: {
    weekly: null,
    yearly: null,
    total: null,
  },
  standard: {
    weekly: null,
    yearly: null,
    total: null,
  },
  advanced: {
    weekly: null,
    yearly: null,
    total: null,
  },
};
